import React, { useRef, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Icon } from "../../components/Component";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Utils";

interface PaymentPdfGeneratorProps {
  paymentData: any;
  orgLogo?: string;
  orgName?: string;
  getPdfUrl?: (blob: Blob) => void;
  triggerAttribute?: string;
  onPdfGenerated?: () => void;
}

const PaymentPdfGenerator: React.FC<PaymentPdfGeneratorProps> = ({
  paymentData,
  orgLogo,
  orgName = "Your Organization",
  getPdfUrl,
  triggerAttribute,
  onPdfGenerated,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (download: boolean = false) => {
    console.log("=== Payment PDF Generation Started ===");
    console.log("paymentData:", paymentData);
    console.log("componentRef.current:", componentRef.current);

    if (!componentRef.current) {
      console.error("componentRef.current is null!");
      return;
    }

    try {
      setIsGenerating(true);

      const element = componentRef.current;

      console.log("Element dimensions:", {
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
        innerHTML_length: element.innerHTML.length,
      });

      // Wait for any pending renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Use html2canvas to capture the component
      console.log("Starting html2canvas...");
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: true,
        onclone: (clonedDoc, clonedElement) => {
          console.log("onclone called, clonedElement:", {
            offsetWidth: clonedElement.offsetWidth,
            offsetHeight: clonedElement.offsetHeight,
          });

          // Ensure the cloned element is visible
          clonedElement.style.display = "block";
          clonedElement.style.visibility = "visible";
          clonedElement.style.position = "relative";
          clonedElement.style.left = "0";
          clonedElement.style.top = "0";

          const style = clonedDoc.createElement("style");
          style.innerHTML = `
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            [data-pdf-template] {
              display: block !important;
              visibility: visible !important;
            }
          `;
          clonedDoc.head.appendChild(style);
        },
      });

      console.log("html2canvas completed. Canvas dimensions:", {
        width: canvas.width,
        height: canvas.height,
      });

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate dimensions
      const marginTop = 8;
      const marginBottom = 10;
      const marginSide = 5;
      const contentWidth = pdfWidth - marginSide * 2;
      const contentHeight = pdfHeight - marginTop - marginBottom;

      // Convert canvas dimensions to PDF dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Calculate total pages needed - use floor and check remainder
      const minContentThreshold = 50;
      const basePages = Math.floor(imgHeight / contentHeight);
      const remainingHeight = imgHeight - (basePages * contentHeight);
      const remainingPixels = (remainingHeight * canvas.width) / contentWidth;
      const totalPages = remainingPixels > minContentThreshold ? basePages + 1 : Math.max(1, basePages);

      console.log("PDF dimensions:", {
        pdfWidth,
        pdfHeight,
        contentWidth,
        contentHeight,
        imgWidth,
        imgHeight,
        totalPages,
        remainingPixels,
      });

      // Process each page
      for (let page = 0; page < totalPages; page++) {
        console.log(`Processing page ${page + 1} of ${totalPages}`);

        if (page > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image to show on this page
        const sourceY = (page * contentHeight * canvas.width) / contentWidth;
        const sourceHeight = Math.min(
          (contentHeight * canvas.width) / contentWidth,
          canvas.height - sourceY
        );

        console.log(`Page ${page + 1} - sourceY: ${sourceY}, sourceHeight: ${sourceHeight}`);

        // Only render if there's meaningful content
        if (sourceHeight > minContentThreshold) {
          // Create temporary canvas for this page
          const pageCanvas = document.createElement("canvas");
          const pageCtx = pageCanvas.getContext("2d");

          if (pageCtx) {
            // Set canvas size
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;

            // Fill with white background
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

            // Draw the content for this page
            pageCtx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight
            );

            // Convert to image data
            const pageImgData = pageCanvas.toDataURL("image/png", 0.95);

            // Calculate PDF dimensions for this page segment
            const pdfContentHeight = (sourceHeight * contentWidth) / canvas.width;

            // Add image to PDF
            console.log(`Adding image to PDF - page ${page + 1}, dimensions: ${contentWidth}x${pdfContentHeight}`);
            pdf.addImage(
              pageImgData,
              "PNG",
              marginSide,
              marginTop,
              contentWidth,
              pdfContentHeight,
              undefined,
              "FAST"
            );
            console.log(`Image added to page ${page + 1}`);
          }
        }

        // Add page number at the bottom
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${page + 1} of ${totalPages}`,
          pdfWidth - 30,
          pdfHeight - 5
        );
      }

      console.log("=== Payment PDF Generation Completed ===");

      // Generate blob
      const generatedPdfBlob = pdf.output("blob");
      console.log("PDF Blob size:", generatedPdfBlob.size, "bytes");
      setPdfBlob(generatedPdfBlob);

      // Create URL for preview
      const url = URL.createObjectURL(generatedPdfBlob);
      setPdfUrl(url);

      // Generate blob for email attachment if callback provided
      if (getPdfUrl) {
        getPdfUrl(generatedPdfBlob);
      }

      if (download) {
        // Download the PDF
        pdf.save(
          `Payment_${paymentData?.paymentVoucherNumber || 'Voucher'}_${dayjs().format("DDMMYYYY")}.pdf`
        );
      }

      setIsGenerating(false);
      return generatedPdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsGenerating(false);
      throw error;
    }
  };

  const handlePreview = async () => {
    try {
      await generatePDF(false);
      setShowPreview(true);
      // Notify parent that PDF generation is complete
      if (onPdfGenerated) {
        onPdfGenerated();
      }
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      // Also notify on error to hide loading
      if (onPdfGenerated) {
        onPdfGenerated();
      }
    }
  };

  // Helper function to restore body scroll
  const restoreBodyScroll = () => {
    // Use setTimeout to ensure modal has fully closed
    setTimeout(() => {
      // Remove the style attribute entirely to restore to normal state
      document.body.removeAttribute("style");
      document.body.classList.remove("modal-open");
      // Remove any lingering backdrop
      const backdrops = document.querySelectorAll(".modal-backdrop");
      backdrops.forEach((backdrop) => backdrop.remove());
    }, 300);
  };

  const handleDownload = async () => {
    try {
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Payment_${paymentData?.paymentVoucherNumber || 'Voucher'}_${dayjs().format("DDMMYYYY")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        await generatePDF(true);
      }
      setShowPreview(false);
      restoreBodyScroll();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    setPdfBlob(null);
    restoreBodyScroll();
  };

  const getStatusColor = (status: string) => {
    const statusColors: any = {
      Draft: "#6c757d",
      Submitted: "#0dcaf0",
      Checked: "#fa8c16",
      Verified: "#fa8c16",
      Approved: "#198754",
      Posted: "#722ed1",
      Paid: "#52c41a",
      Rejected: "#dc3545",
      Returned: "#722ed1",
    };
    return statusColors[status] || "#6c757d";
  };

  const getPaymentStatusColor = (status: string) => {
    if (status === "Paid") return "#52c41a";
    if (status === "Partially Paid") return "#fa8c16";
    return "#dc3545";
  };

  // Get reference label based on payment method
  const getReferenceLabel = (paymentMethod: string) => {
    if (paymentMethod === "Cheque") return "Cheque Number";
    if (["NEFT", "RTGS", "IMPS"].includes(paymentMethod)) return "UTR Number";
    return "Transaction Number";
  };

  return (
    <>
      {/* PDF Generation Button */}
      <Button
        className="btn btn-white btn-outline-light"
        onClick={handlePreview}
        disabled={isGenerating}
        {...(triggerAttribute ? { [triggerAttribute]: "true" } : {})}
      >
        {isGenerating ? (
          <>
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="sr-only">Loading...</span>
            </div>
            Generating...
          </>
        ) : (
          <>
            <Icon name="download" />
            <span>Export PDF</span>
          </>
        )}
      </Button>

      {/* PDF Preview Modal */}
      <Modal
        isOpen={showPreview}
        toggle={closePreview}
        size="xl"
        className="modal-dialog-centered"
        backdrop="static"
        scrollable={true}
        onClosed={() => {
          // This runs after modal animation completes - add delay to ensure it's the last thing
          setTimeout(() => {
            document.body.removeAttribute("style");
          }, 100);
        }}
      >
        <ModalHeader toggle={closePreview}>
          <div className="d-flex align-items-center">
            <Icon name="file-docs" className="me-2" />
            Payment PDF Preview - {paymentData?.paymentVoucherNumber}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0, maxHeight: "70vh", overflow: "auto" }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted small">
              <Icon name="info" className="me-1" />
              Preview generated on {dayjs().format("DD-MMM-YYYY HH:mm")}
            </div>
            <div>
              <Button color="primary" onClick={handleDownload}>
                <Icon name="download" className="me-1" />
                Download PDF
              </Button>
              <Button color="secondary" onClick={closePreview} className="ms-2">
                Close
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Hidden PDF Template */}
      <div
        ref={componentRef}
        data-pdf-template="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "0",
          width: "210mm",
          minHeight: "297mm",
          padding: "15px",
          backgroundColor: "white",
          fontFamily: "'Arial', sans-serif",
          fontSize: "11px",
          lineHeight: "1.4",
          color: "#333",
          zIndex: -1000,
        }}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: "2px solid #1890ff",
            paddingBottom: "12px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {orgLogo && (
              <img
                src={orgLogo}
                alt={orgName}
                style={{
                  height: "40px",
                  marginRight: "12px",
                }}
              />
            )}
            <div>
              <h1
                style={{
                  color: "#333",
                  margin: "0",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {orgName}
              </h1>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <h2
              style={{
                color: "#1890ff",
                margin: "0 0 4px 0",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              PAYMENT VOUCHER
            </h2>
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              {paymentData?.paymentVoucherNumber}
            </div>
            <div style={{ fontSize: "10px", color: "#666" }}>
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: getStatusColor(paymentData?.status),
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "3px",
                  fontSize: "9px",
                }}
              >
                {paymentData?.status || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Section 1: Payment Details */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Payment Details
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px",
              border: "1px solid #f0f0f0",
            }}
          >
            <tbody>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                  Payment Voucher #
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {paymentData?.paymentVoucherNumber || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                  Payment Date
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {paymentData?.paymentDate ? dayjs(paymentData.paymentDate).format("DD-MMM-YYYY") : "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Payment Type
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {paymentData?.paymentType || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Payment Method
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {paymentData?.paymentMethod || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Contractor
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {paymentData?.contractorName || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Site
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {paymentData?.siteName || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Status
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: getStatusColor(paymentData?.status),
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {paymentData?.status || "-"}
                  </span>
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  {paymentData?.paymentMethod && paymentData.paymentMethod !== "Cash" ? getReferenceLabel(paymentData.paymentMethod) : "Reference"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <code style={{ backgroundColor: "#f5f5f5", padding: "2px 4px", borderRadius: "2px" }}>
                    {paymentData?.utrReference || paymentData?.utrNumber || paymentData?.chequeNumber || paymentData?.transactionNumber || "-"}
                  </code>
                </td>
              </tr>
              {(paymentData?.bankName || paymentData?.bankAccountNumber) && (
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Bank Name
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {paymentData?.bankName || "-"}
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Bank Account
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {paymentData?.bankAccountNumber || "-"}
                  </td>
                </tr>
              )}
              {paymentData?.narration && (
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Narration
                  </td>
                  <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    {paymentData.narration}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Section 2: Payment Summary */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Payment Summary
          </div>
          <div
            style={{
              padding: "12px",
              border: "1px solid #f0f0f0",
              borderRadius: "4px",
              backgroundColor: "#fff",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
              <span>Total Payment Amount:</span>
              <strong>{formatCurrency(paymentData?.totalPaymentAmount || 0, "INR")}</strong>
            </div>

            {paymentData?.tdsDeducted > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
                <span>TDS Deducted ({paymentData?.tdsSection} - {paymentData?.tdsRate}%):</span>
                <strong style={{ color: "#dc3545" }}>-{formatCurrency(paymentData?.tdsDeducted || 0, "INR")}</strong>
              </div>
            )}

            {paymentData?.otherDeductions > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "10px" }}>
                <span>Other Deductions:</span>
                <strong style={{ color: "#dc3545" }}>-{formatCurrency(paymentData?.otherDeductions || 0, "INR")}</strong>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "12px",
                borderTop: "1px solid #d9d9d9",
                paddingTop: "8px",
                marginTop: "8px",
              }}
            >
              <span><strong>Net Payable Amount:</strong></span>
              <strong style={{ color: "#52c41a", fontSize: "14px" }}>
                {formatCurrency(paymentData?.netPayableAmount || paymentData?.paidAmount || 0, "INR")}
              </strong>
            </div>
          </div>
        </div>

        {/* Section 3: Payment Allocations */}
        {paymentData?.allocations && paymentData.allocations.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Payment Allocations ({paymentData.allocations.length})
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "9px",
                pageBreakInside: "auto",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "15%" }}>RA Bill #</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "15%" }}>Work Order</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "20%" }}>RA Period</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "12%" }}>Bill Value</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "12%" }}>Already Paid</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "12%" }}>This Payment</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "center", fontWeight: "bold", width: "14%" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentData.allocations.map((allocation: any, index: number) => (
                  <tr key={index} style={{ pageBreakInside: "avoid" }}>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", fontWeight: "bold" }}>
                      {allocation.raBillNumber || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>
                      {allocation.workOrderNumber || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>
                      {allocation.raPeriodFrom && allocation.raPeriodTo ? (
                        `${dayjs(allocation.raPeriodFrom).format("DD-MMM-YYYY")} to ${dayjs(allocation.raPeriodTo).format("DD-MMM-YYYY")}`
                      ) : "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {formatCurrency(allocation.raBillNetPayable || 0, "INR")}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      {formatCurrency(allocation.totalPaidAmount || 0, "INR")}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right", fontWeight: "bold", color: "#1890ff" }}>
                      {formatCurrency(allocation.paidAmount || allocation.thisPaymentAmount || 0, "INR")}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: getPaymentStatusColor(allocation.paymentStatus),
                          color: "white",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {allocation.paymentStatus || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 4: Approval History */}
        {paymentData?.approvalHistory && paymentData.approvalHistory.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Approval History
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "9px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "15%" }}>Action</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "20%" }}>By</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "15%" }}>Role</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "15%" }}>Date</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "35%" }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {paymentData.approvalHistory.map((history: any, index: number) => (
                  <tr key={index}>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: history.action === "Approved" ? "#52c41a" : history.action === "Rejected" ? "#dc3545" : "#1890ff",
                          color: "white",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {history.action || history.status || "-"}
                      </span>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", fontWeight: "bold" }}>
                      {history.actionBy || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      {history.role || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      {history.actionDate ? dayjs(history.actionDate).format("DD-MMM-YYYY HH:mm") : "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      {history.comments || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Section 5: Created/Processed Info */}
        {(paymentData?.createdBy || paymentData?.processedBy) && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Audit Information
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "10px",
                border: "1px solid #f0f0f0",
              }}
            >
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                    Created By
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                    {paymentData?.createdBy || "-"}
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                    Created At
                  </td>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                    {paymentData?.createdAt ? dayjs(paymentData.createdAt).format("DD-MMM-YYYY HH:mm") : "-"}
                  </td>
                </tr>
                {paymentData?.processedBy && (
                  <tr>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                      Processed By
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                      {paymentData.processedBy}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                      Processed At
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                      {paymentData?.processedAt ? dayjs(paymentData.processedAt).format("DD-MMM-YYYY HH:mm") : "-"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: "10px",
            textAlign: "center",
            fontSize: "9px",
            color: "#999",
            marginTop: "20px",
          }}
        >
          Generated on {dayjs().format("DD-MMM-YYYY HH:mm")} | © {dayjs().format("YYYY")} {orgName}
        </div>
      </div>
    </>
  );
};

export default PaymentPdfGenerator;
